import React, { useState, useEffect } from "react";
import { useModalState } from "../helpers/ModalProvider";
import { Card, Flex, IconButton, Table, Text } from "@chakra-ui/react";
import { LuDollarSign, LuFileSpreadsheet, LuTrash } from "react-icons/lu";
import { Button } from "../components/ui/button";
import {
  getPrepaidKeys,
  getPrepaidKeysForDisplay,
  removePrepaidKey,
} from "../helpers/prepaidKeyManager";
import type { PrepaidKey } from "../helpers/prepaidKeyManager";

// Re-export from the centralized manager so existing imports keep working.
// New code should import directly from "../helpers/prepaidKeyManager".
export { getPrepaidKeys, savePrepaidKey, removePrepaidKey, updatePrepaidKeyCredits } from "../helpers/prepaidKeyManager";
export type { PrepaidKey } from "../helpers/prepaidKeyManager";

/**
 * Display-only shape for the prepaid keys table. Intentionally omits the full
 * (deobfuscated) key so it is never held in React state -- see the security
 * notes in ../helpers/prepaidKeyManager.ts for rationale.
 */
type PrepaidKeyDisplay = Omit<PrepaidKey, "key"> & { displayKey: string };

const PrepaidKeysContainer: React.FC = () => {
  const {
    showCreatePrepaidModal,
    showRefillPrepaidModal,
  } = useModalState();

  // IMPORTANT: We store ONLY the display-safe projection in state. Full key
  // values are deobfuscated on-demand inside action handlers (refill/delete)
  // and immediately discarded, so they never live in the React tree.
  const [prepaidKeys, setPrepaidKeys] = useState<PrepaidKeyDisplay[]>(
    () => getPrepaidKeysForDisplay()
  );

  // Re-read from sessionStorage when it changes (e.g. after create/refill/delete in modals)
  useEffect(() => {
    const handleStorage = () => setPrepaidKeys(getPrepaidKeysForDisplay());
    window.addEventListener("storage", handleStorage);
    // Also listen for a custom event so same-tab updates work
    window.addEventListener("prepaidKeysUpdated", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("prepaidKeysUpdated", handleStorage);
    };
  }, []);

  /**
   * Resolve the full (deobfuscated) key for the row at `index` without
   * persisting it in component state. The caller should use the return value
   * immediately and let it fall out of scope.
   */
  const resolveFullKey = (index: number): string | undefined => {
    const fullKeys = getPrepaidKeys();
    return fullKeys[index]?.key;
  };

  const handleRefillClick = (index: number) => {
    const fullKey = resolveFullKey(index);
    if (!fullKey) return;
    showRefillPrepaidModal(fullKey);
  };

  const handleDeleteClick = (index: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this prepaid key? This action cannot be undone."
      )
    ) {
      return;
    }
    const fullKey = resolveFullKey(index);
    if (!fullKey) return;
    removePrepaidKey(fullKey);
    setPrepaidKeys(getPrepaidKeysForDisplay());
  };

  const formatDate = (date: string) => {
    if (!date) return "\u2014";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // TODO: Extract to shared utility — duplicated in APIKeysContainer.tsx
  const formatLastUsed = (date: string) => {
    if (!date) return "Never";
    const now = new Date();
    const lastUsed = new Date(date);
    const diffInSeconds = Math.floor(
      (now.getTime() - lastUsed.getTime()) / 1000
    );

    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return lastUsed.toLocaleDateString("en-CA");
  };

  return (
    <Card.Root>
      <Card.Header>
        <Card.Title fontSize="30px" fontWeight="bold" letterSpacing="-0.4px" lineHeight="32px">
          Pre-paid API keys
        </Card.Title>
        <Card.Description fontSize="md" lineHeight="24px" color="#52525b">
          Generate an API key and pre-purchase credits as you need them using the{" "}
          <Text as="span" fontWeight="bold">x402 protocol</Text>
          . Designed for AI agents, automated features, or simple testing, pre-paid API keys allow you to purchase only the credits you need on-demand, and no CoinCap account is required. Each x402 key has its own pool of purchased credits that are not shared across multiple keys, unlike traditional API keys.
          <br /><br />
          Credits cost $0.002 each — 500 credits per $1 USDC on Base.
        </Card.Description>
      </Card.Header>

      <Card.Body>
        {prepaidKeys.length > 0 && (
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>API Key</Table.ColumnHeader>
                <Table.ColumnHeader>Credits Used</Table.ColumnHeader>
                <Table.ColumnHeader>Credits Available</Table.ColumnHeader>
                <Table.ColumnHeader>Last Used</Table.ColumnHeader>
                <Table.ColumnHeader>Created On</Table.ColumnHeader>
                <Table.ColumnHeader>Actions</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {prepaidKeys.map((k, index) => (
                <Table.Row key={`${k.displayKey}-${k.createdAt}`}>
                  <Table.Cell>
                    <Text fontSize="sm" fontFamily="mono">
                      {k.displayKey}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>{k.creditsUsed?.toLocaleString()}</Table.Cell>
                  <Table.Cell>{k.creditsAvailable?.toLocaleString()}</Table.Cell>
                  <Table.Cell>{formatLastUsed(k.lastUsed)}</Table.Cell>
                  <Table.Cell>{formatDate(k.createdAt)}</Table.Cell>
                  <Table.Cell>
                    <Flex gap={2}>
                      {/* Refill: dollar-sign icon, cyan subtle per Figma.
                          Needs an explicit icon color — without one it
                          inherits the default (near-white) and disappears
                          against the pale cyan fill. */}
                      <IconButton
                        size="sm"
                        bg="#cffafe"
                        border="1px solid"
                        borderColor="#a5f3fc"
                        color="#0e7490"
                        _hover={{ bg: "#a5f3fc" }}
                        transition="background 150ms"
                        onClick={() => handleRefillClick(index)}
                        aria-label="Refill key"
                      >
                        <LuDollarSign />
                      </IconButton>
                      {/* Delete: trash icon, pink subtle per Figma */}
                      <IconButton
                        size="sm"
                        bg="#fce7f3"
                        border="1px solid"
                        borderColor="#fbcfe8"
                        color="#db2777"
                        _hover={{ bg: "#fbcfe8" }}
                        transition="background 150ms"
                        onClick={() => handleDeleteClick(index)}
                        aria-label="Delete key"
                      >
                        <LuTrash />
                      </IconButton>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}

        {/* Button Frame — bottom right per Figma */}
        <Flex gap={2} justifyContent="flex-end" pt={2} width="full">
          <Button
            bg="#f3e8ff"
            border="1px solid"
            borderColor="#e9d5ff"
            color="#641ba3"
            onClick={() => window.open("/api-docs/", "_blank", "noopener,noreferrer")}
          >
            <LuFileSpreadsheet />
            API Docs
          </Button>
          <Button
            variant="solid"
            colorPalette="purple"
            onClick={() => showCreatePrepaidModal()}
          >
            Create a pre-paid API key
          </Button>
        </Flex>
      </Card.Body>
    </Card.Root>
  );
};

export default PrepaidKeysContainer;
